import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowRight, Search, ShieldCheck } from 'lucide-react';
import { verifyCertificate, type CertificateVerification } from '../lib/certificates';
import { PageHeader, Panel, Stamp } from '../components/lms/ui';
import { Skeleton } from '../components/lms/Skeletons';
import { marketingHref } from '../lib/surface';

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('en-NG', { year: 'numeric', month: 'long', day: 'numeric' });

const VerifyCertificate: React.FC = () => {
  const { code: routeCode } = useParams();
  const [code, setCode] = useState(routeCode || '');
  const [result, setResult] = useState<CertificateVerification | null | 'notfound'>(null);
  const [busy, setBusy] = useState(false);
  const [searched, setSearched] = useState(false);

  const check = async (c?: string) => {
    const v = (c ?? code).trim();
    if (!v) return;
    setBusy(true);
    setSearched(true);
    try {
      const r = await verifyCertificate(v);
      setResult(r ?? 'notfound');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (routeCode) void check(routeCode);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [routeCode]);

  return (
    <div className="mx-auto max-w-[1000px]">
      <PageHeader
        title="Certificate register"
        lede="Check a certificate against the LSEM register. Every code below was issued to a student who completed a programme."
        actions={
          <a href={marketingHref('/courses')} className="lms-btn-secondary">
            Programme register <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
          </a>
        }
      />

      <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start">
        <div className="space-y-6">
          <Panel>
            <div className="border-b border-rule px-5 py-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-ink-2">Public record search</p>
                  <h2 className="mt-1 text-[17px] font-semibold tracking-[-0.015em] text-ink">Enter a certificate code</h2>
                </div>
                <Stamp tone="neutral">LSEM register</Stamp>
              </div>
              <form
                onSubmit={(e) => { e.preventDefault(); void check(); }}
                className="mt-4 flex flex-col gap-2 sm:flex-row"
              >
                <label className="relative block flex-1">
                  <span className="sr-only">Certificate code</span>
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-2" strokeWidth={1.5} />
                  <input
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="A1B2C3D4E5"
                    autoComplete="off"
                    spellCheck={false}
                    className="lms-field pl-9 font-mono uppercase tracking-[0.08em]"
                  />
                </label>
                <button type="submit" disabled={busy || !code.trim()} className="lms-btn-primary sm:w-auto">
                  {busy ? 'Checking…' : 'Check register'}
                  <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </form>
            </div>

            <div className="min-h-[220px] px-5 py-5">
              {busy ? (
                <div className="space-y-4" role="status" aria-busy="true" aria-label="Checking the certificate register">
                  <span className="sr-only">Checking the certificate register…</span>
                  <div className="space-y-2 border-b border-rule pb-3">
                    <Skeleton className="h-2.5 w-24 rounded-md" />
                    <Skeleton className="h-6 w-56 rounded-md" />
                  </div>
                  <div className="space-y-3">
                    {[0, 1, 2, 3].map((i) => (
                      <div key={i} className="grid gap-2 sm:grid-cols-[140px_minmax(0,1fr)] sm:gap-4">
                        <Skeleton className="h-2.5 w-24 rounded-md" />
                        <Skeleton className="h-3.5 w-3/5 rounded-md" />
                      </div>
                    ))}
                  </div>
                </div>
              ) : !searched ? (
                <div className="flex h-full min-h-[180px] flex-col justify-center">
                  <ShieldCheck className="h-8 w-8 text-pine" strokeWidth={1.25} />
                  <p className="mt-3 text-[14px] font-semibold text-ink">No code checked yet</p>
                  <p className="mt-1 max-w-[46ch] text-[12.5px] leading-5 text-ink-2">
                    The code is printed on the certificate, below the award title. Verification is public and does not
                    require an LSEM account.
                  </p>
                </div>
              ) : null}

              {!busy && searched && result === 'notfound' && (
                <div className="border border-warning/25 bg-warning-soft p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-[14px] font-semibold text-warning">No certificate found</p>
                    <Stamp tone="held">Unmatched</Stamp>
                  </div>
                  <p className="mt-1.5 text-[12.5px] leading-5 text-warning">
                    No record matches that code. Check the code character by character, including hyphens. If it still
                    does not match, contact the student desk at{' '}
                    <a href="mailto:info@lsem.org.ng" className="font-semibold underline underline-offset-2">info@lsem.org.ng</a>.
                  </p>
                </div>
              )}

              {!busy && searched && result && result !== 'notfound' && result.revoked && (
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-rule pb-3">
                    <div>
                      <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-ink-2">Record status</p>
                      <p className="mt-1 text-[18px] font-semibold tracking-[-0.015em] text-danger">Certificate revoked</p>
                    </div>
                    <Stamp tone="held">Revoked</Stamp>
                  </div>
                  <dl className="mt-4 divide-y divide-rule border-y border-rule">
                    <div className="grid gap-1 py-3 sm:grid-cols-[140px_minmax(0,1fr)] sm:gap-4">
                      <dt className="font-mono text-[9px] uppercase tracking-[0.12em] text-ink-2">Programme</dt>
                      <dd className="text-[13px] font-medium text-ink">{result.course_title || result.course_id}</dd>
                    </div>
                    <div className="grid gap-1 py-3 sm:grid-cols-[140px_minmax(0,1fr)] sm:gap-4">
                      <dt className="font-mono text-[9px] uppercase tracking-[0.12em] text-ink-2">Code</dt>
                      <dd className="font-mono text-[12px] text-ink">{result.code}</dd>
                    </div>
                    <div className="grid gap-1 py-3 sm:grid-cols-[140px_minmax(0,1fr)] sm:gap-4">
                      <dt className="font-mono text-[9px] uppercase tracking-[0.12em] text-ink-2">Original issue</dt>
                      <dd className="text-[13px] text-ink">{formatDate(result.issued_at)}</dd>
                    </div>
                    {result.revoked_at && (
                      <div className="grid gap-1 py-3 sm:grid-cols-[140px_minmax(0,1fr)] sm:gap-4">
                        <dt className="font-mono text-[9px] uppercase tracking-[0.12em] text-ink-2">Revoked</dt>
                        <dd className="text-[13px] text-ink">{formatDate(result.revoked_at)}</dd>
                      </div>
                    )}
                  </dl>
                  {result.revoked_reason && (
                    <div className="mt-4 border border-danger/25 bg-danger-soft px-4 py-3">
                      <p className="font-mono text-[9px] uppercase tracking-[0.12em] text-danger">Reason on record</p>
                      <p className="mt-1 text-[12.5px] leading-5 text-danger">{result.revoked_reason}</p>
                    </div>
                  )}
                </div>
              )}

              {!busy && searched && result && result !== 'notfound' && !result.revoked && (
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-rule pb-3">
                    <div>
                      <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-ink-2">Record status</p>
                      <p className="mt-1 text-[18px] font-semibold tracking-[-0.015em] text-success">Certificate valid</p>
                    </div>
                    <Stamp tone="cleared">Verified</Stamp>
                  </div>
                  <dl className="mt-4 divide-y divide-rule border-y border-rule">
                    <div className="grid gap-1 py-3 sm:grid-cols-[140px_minmax(0,1fr)] sm:gap-4">
                      <dt className="font-mono text-[9px] uppercase tracking-[0.12em] text-ink-2">Programme</dt>
                      <dd className="text-[13px] font-medium text-ink">{result.course_title || result.course_id}</dd>
                    </div>
                    <div className="grid gap-1 py-3 sm:grid-cols-[140px_minmax(0,1fr)] sm:gap-4">
                      <dt className="font-mono text-[9px] uppercase tracking-[0.12em] text-ink-2">Code</dt>
                      <dd className="font-mono text-[12px] text-ink">{result.code}</dd>
                    </div>
                    <div className="grid gap-1 py-3 sm:grid-cols-[140px_minmax(0,1fr)] sm:gap-4">
                      <dt className="font-mono text-[9px] uppercase tracking-[0.12em] text-ink-2">Issued</dt>
                      <dd className="text-[13px] text-ink">{formatDate(result.issued_at)}</dd>
                    </div>
                    <div className="grid gap-1 py-3 sm:grid-cols-[140px_minmax(0,1fr)] sm:gap-4">
                      <dt className="font-mono text-[9px] uppercase tracking-[0.12em] text-ink-2">Issuing school</dt>
                      <dd className="text-[13px] text-ink">Lagos School of Export Management</dd>
                    </div>
                  </dl>
                  <p className="mt-3 font-mono text-[9px] uppercase leading-4 tracking-[0.1em] text-ink-2">
                    Register record · checked {new Date().toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                </div>
              )}
            </div>
          </Panel>
        </div>

        <aside className="space-y-6">
          <Panel>
            <div className="border-b border-rule px-5 py-3.5">
              <h2 className="text-[13px] font-semibold text-ink">Using the register</h2>
            </div>
            <ol className="divide-y divide-rule">
              {[
                ['01', 'Ask the holder for the code printed on the certificate.'],
                ['02', 'Enter it exactly as printed, without spaces.'],
                ['03', 'A valid record shows the programme and issue date.'],
              ].map(([n, copy]) => (
                <li key={n} className="grid grid-cols-[28px_minmax(0,1fr)] gap-2 px-5 py-3.5">
                  <span className="font-mono text-[9.5px] text-ink-2">{n}</span>
                  <span className="text-[12px] leading-5 text-ink-2">{copy}</span>
                </li>
              ))}
            </ol>
          </Panel>

          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-800 via-blue-700 to-sky-600 px-5 py-5 text-white shadow-lg shadow-blue-900/10">
            <div aria-hidden="true" className="absolute -right-8 -top-10 h-28 w-28 rounded-full border border-white/15" />
            <div className="relative">
              <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-blue-200">Need help?</p>
              <p className="mt-2 text-[14px] font-semibold leading-6">Certificate queries go to the student desk.</p>
              <a href="mailto:info@lsem.org.ng" className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-semibold text-white underline decoration-white/30 underline-offset-4 hover:decoration-white">
                info@lsem.org.ng
              </a>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default VerifyCertificate;
